import { Unit } from "src/api/sure-api/ssq/useGetOneSsqVersion";
import { NumberType, getNumberType, getDecimalPlaces, shouldUseGrouping } from "./numberTypes";

type FormatNumberIntlOptions = Intl.NumberFormatOptions & { withPrecisionFloor?: boolean };

type VersionNumber = {
    versionMajor: number;
    versionMinor: number;
};

export const formatVersion = (version: VersionNumber): string => `v${version.versionMajor}.${version.versionMinor}`;

export const formatNumber = (
    value: string | number,
    language?: string,
    allOptions?: FormatNumberIntlOptions,
): string => {
    const numericValue = Number(value);
    const locale = language || "en";

    const { withPrecisionFloor = true, ...options } = allOptions || {};

    // Format with provided options
    const formatter = new Intl.NumberFormat(locale, options);

    const usePrecisionFloor = withPrecisionFloor && numericValue !== 0;
    if (usePrecisionFloor) {
        const precision = options.maximumFractionDigits ?? 3;
        // Calculate the "floor": e.g., if precision is 3, floor is 0.001
        const precisionFloor = Math.pow(10, -precision);

        // If the value is positive, smaller than the floor, and not zero
        if (numericValue > 0 && numericValue < precisionFloor) {
            return `< ${formatter.format(precisionFloor)}`;
        }

        // If the value is negative and larger than -floor (e.g., -0.0009)
        if (numericValue < 0 && numericValue > -precisionFloor) {
            return `> ${formatter.format(-precisionFloor)}`;
        }
    }

    const formattedValue = formatter.format(numericValue);

    return formattedValue;
};

/**
 * Parses a localized number string and returns it in standard format (with dot as decimal separator)
 * Handles various locales: French (1 234,56), English (1,234.56), etc.
 * @param value The localized number string
 * @param locale The locale to use for parsing (defaults to 'en')
 * @returns The number in standard format as a string, or empty string if invalid
 */
export const parseLocalizedNumber = (value: string, locale: string = "en"): string => {
    if (!value || value.trim() === "") {
        return "";
    }

    // Get the decimal and grouping separators for the locale
    const parts = new Intl.NumberFormat(locale).formatToParts(12345.6);
    const decimalSeparator = parts.find((part) => part.type === "decimal")?.value || ".";
    const groupSeparator = parts.find((part) => part.type === "group")?.value || ",";

    // Remove all grouping separators
    let normalized = value.replace(new RegExp(`\\${groupSeparator}`, "g"), "");

    // Replace non-breaking spaces (often used in French) with nothing
    normalized = normalized.replace(/\u00A0/g, "").replace(/\s/g, "");

    // Replace the decimal separator with a dot
    if (decimalSeparator !== ".") {
        normalized = normalized.replace(decimalSeparator, ".");
    }

    // Match the decimal syntax accepted by numeric inputs while excluding JavaScript-only
    // values such as hexadecimal literals and Infinity. Scientific notation remains valid.
    const decimalNumberPattern = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/;
    const numericValue = Number(normalized);
    if (!decimalNumberPattern.test(normalized) || !Number.isFinite(numericValue)) {
        return "";
    }

    return normalized;
};

export const isValidStandardNumber = (value: string | number): boolean => {
    return parseLocalizedNumber(String(value), "en") !== "" && Number.isFinite(Number(value));
};

/**
 * Formats a number according to its type and locale
 * @param value The numeric value to format
 * @param unitTitle The unit title from SSQ property
 * @param locale The locale to use for formatting
 * @param maxFractionDigits Optional override for maximum fraction digits
 * @returns Formatted number string
 */
export const formatNumberByType = (
    value: string | number,
    unitTitle?: string,
    locale: string = "en",
    maxFractionDigits?: number,
    withPrecisionFloor?: boolean,
): string => {
    if (value === "" || value === null || value === undefined) {
        return "";
    }
    const numericValue = typeof value === "string" ? parseFloat(value) : value;

    if (isNaN(numericValue)) {
        // For non-numeric strings, return them as-is only if the type is non-numeric
        const numberType = getNumberType(unitTitle);
        if (numberType === NumberType.NON_NUMERIC) {
            return String(value);
        }
        return "";
    }

    // A missing unit does not mean the value is not a number: unitless numeric SSQ
    // properties are counts and amounts ("nombre de salariés", "coûts de location"),
    // and they need the thousands separator too. Non-numeric values were already
    // returned as-is by the isNaN branch above. [PF-1246]
    const numberType = getNumberType(unitTitle);

    const decimalPlaces = maxFractionDigits !== undefined ? maxFractionDigits : getDecimalPlaces(numberType);
    const useGrouping = shouldUseGrouping(numberType);

    const options: Intl.NumberFormatOptions = {
        minimumFractionDigits: 0, // Ne jamais forcer l'affichage des décimales
        maximumFractionDigits: decimalPlaces,
        useGrouping: useGrouping,
    };

    // Special handling for percentages
    if (numberType === NumberType.PERCENTAGE) {
        // Don't use style: 'percent' as it multiplies by 100
        // We assume the value is already in percentage form (e.g., 50 for 50%)
        options.useGrouping = false;
    }

    // Format with provided options
    const formatter = new Intl.NumberFormat(locale, options);
    const usePrecisionFloor = (withPrecisionFloor ?? true) && numericValue !== 0;
    if (usePrecisionFloor) {
        const precision = options.maximumFractionDigits ?? 3;
        // Calculate the "floor": e.g., if precision is 3, floor is 0.001
        const precisionFloor = Math.pow(10, -precision);

        // If the value is positive, smaller than the floor, and not zero
        if (numericValue > 0 && numericValue < precisionFloor) {
            return `< ${formatter.format(precisionFloor)}`;
        }

        // If the value is negative and larger than -floor (e.g., -0.0009)
        if (numericValue < 0 && numericValue > -precisionFloor) {
            return `> ${formatter.format(-precisionFloor)}`;
        }
    }

    const formattedValue = formatter.format(numericValue);
    return formattedValue;
};

export const formatDateTime = (
    value: Date | number,
    language?: string,
    options?: Intl.DateTimeFormatOptions,
): string => {
    return Intl.DateTimeFormat(language, options).format(value);
};

/**
 * Formats a date string to a localized date
 * @param dateString The date string to format
 * @returns A formatted date string or an empty string if invalid
 */
export const formatDate = (dateString?: string): string => {
    if (!dateString) return "";

    try {
        return new Date(dateString).toLocaleDateString();
    } catch (error) {
        return "";
    }
};

export const formatStringForBackend = (value: string | undefined | null): string | null => {
    if (value === undefined || value === null || value.replaceAll(" ", "") === "") {
        return null;
    }

    return value.trim();
};

type ThresholdRange = {
    low: number;
    high: number;
};

type Options = {
    maxLength: number;
    separator?: string;
};
export const limitStringList = (value: string[], options: Options): string => {
    // Deprecated algorithm.
    // Keeping it here in case we want it.
    // (kilian) note to self : better delete it at once ? idk
    const { maxLength, separator = "," } = options;

    if (maxLength === 0) {
        return "";
    }

    const totalLength = value.join("").length;
    if (maxLength > totalLength) {
        return value.join(separator);
    }

    let currentMax = 0;
    const wordThresholds = value.reduce((previousValue, currentWord) => {
        const newMax = currentMax + currentWord.length;
        const thresholdRange: ThresholdRange = {
            low: currentMax,
            high: newMax,
        };
        currentMax = newMax;
        previousValue.push(thresholdRange);

        return previousValue;
    }, [] as ThresholdRange[]);

    const limitPosition = wordThresholds.findIndex((value) => value.low < maxLength && maxLength <= value.high);
    const wordsToDisplay = limitPosition + 1;
    const leftoverWords = wordThresholds.length - wordsToDisplay;

    const isLastWordFullyDisplayed = wordThresholds[limitPosition].high === maxLength;
    const ellipsis = isLastWordFullyDisplayed ? "" : "…";

    const separatorLength = wordsToDisplay * separator.length;
    const valueListAsString = value.join(separator).slice(0, maxLength - 1 + separatorLength);
    const leftoverDisplay = leftoverWords ? `+${leftoverWords}` : ``;
    const stringToDisplay = `${valueListAsString}${ellipsis} ${leftoverDisplay}`;

    return stringToDisplay;
};

export const formatUnitAbbreviation = (unit?: Unit): string => {
    return unit?.abbreviation ?? "";
};

export const formatConversionFactor = (factor: number, locale: string): string =>
    new Intl.NumberFormat(locale, { maximumSignificantDigits: 6 }).format(factor);
